import React, { useMemo } from 'react';
import { Alert, Button, Empty, Image, Spin, Tag, Typography } from 'antd';
import { CloudOutlined, FileImageOutlined } from '@ant-design/icons';
import {
  useLatestParsedWeatherBulletin,
  type BulletinPrecipRegion,
  type BulletinPrecipRisk,
} from '@/api/weatherBulletinParsed';

const { Text } = Typography;

const riskLabel: Record<BulletinPrecipRisk, string> = {
  none: '无明显降水',
  light: '弱降水',
  moderate: '中等降水',
  heavy: '强降水',
};

const riskColor: Record<BulletinPrecipRisk, string> = {
  none: 'default',
  light: 'green',
  moderate: 'orange',
  heavy: 'red',
};

const textSeverityColor: Record<'low' | 'medium' | 'high', string> = {
  low: 'default',
  medium: 'gold',
  high: 'volcano',
};

const riskOrder: Record<BulletinPrecipRisk, number> = {
  none: 0,
  light: 1,
  moderate: 2,
  heavy: 3,
};

function fmtPeriod(from?: string | null, to?: string | null): string {
  if (!from || !to) return '有效时段未识别';
  return `${from} 至 ${to}`;
}

function sortRegions(regions: BulletinPrecipRegion[]): BulletinPrecipRegion[] {
  return [...regions].sort((a, b) => {
    const riskDiff = riskOrder[b.risk] - riskOrder[a.risk];
    if (riskDiff !== 0) return riskDiff;
    return (b.coverage ?? 0) - (a.coverage ?? 0);
  });
}

const WeatherBulletinValidationCard: React.FC = () => {
  const { data, isLoading, error } = useLatestParsedWeatherBulletin();

  const regions = useMemo(() => {
    return sortRegions(data?.precip_24h?.regions ?? []).filter((r) => r.risk !== 'none').slice(0, 8);
  }, [data]);
  const textSignal = data?.text_signal;
  const imageUrl = data?.precip_24h?.available === false ? null : data?.precip_24h?.image_url;

  if (isLoading) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Spin tip="加载气象局简报校验..." />
      </section>
    );
  }

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="气象局简报校验暂不可用"
        description="未读取到已解析的每日气象简报，不影响 IFS 地图和新能源指数使用。"
        style={{ marginBottom: 14 }}
      />
    );
  }

  if (!data) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Empty description="暂无气象局简报解析结果" />
      </section>
    );
  }

  return (
    <section
      style={{
        border: '1px solid #d7e3dc',
        borderRadius: 8,
        background: '#fbfdfb',
        padding: 16,
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 220px', gap: 16, alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <CloudOutlined style={{ color: '#0f766e' }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>气象局简报校验</div>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {data.date} · 降水图有效期：{fmtPeriod(data.precip_24h.valid_from, data.precip_24h.valid_to)}
          </Text>

          {textSignal && (
            <>
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                {textSignal.headline || '简报文字结论'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {textSignal.risk_tags.map((tag) => (
                  <Tag key={`${tag.type}-${tag.label}`} color={textSeverityColor[tag.severity]} style={{ marginRight: 0 }}>
                    {tag.label}
                  </Tag>
                ))}
                {textSignal.key_regions.slice(0, 8).map((region) => (
                  <Tag key={region} style={{ marginRight: 0 }}>
                    {region}
                  </Tag>
                ))}
              </div>
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                {Object.entries({
                  降水: textSignal.conditions.precipitation,
                  云况: textSignal.conditions.cloud,
                  风况: textSignal.conditions.wind,
                  温度: textSignal.conditions.temperature,
                }).map(([label, value]) => (
                  value ? (
                    <div key={label} style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.6 }}>
                      <Text strong style={{ fontSize: 12 }}>{label}</Text>
                      <span style={{ marginLeft: 6 }}>{value}</span>
                    </div>
                  ) : null
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {regions.length ? (
              regions.map((region) => (
                <Tag key={region.name} color={riskColor[region.risk]} style={{ marginRight: 0 }}>
                  {region.name} {riskLabel[region.risk]} 覆盖{Math.round((region.coverage ?? 0) * 100)}%
                </Tag>
              ))
            ) : (
              <Tag>{imageUrl ? "重点城市无明显降水色斑" : "无可用降水预报图，请参考文字预报"}</Tag>
            )}
          </div>

          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(textSignal?.trading_notes ?? []).map((note) => (
              <Text key={note} style={{ fontSize: 13, color: '#374151' }}>
                {note}
              </Text>
            ))}
            {(data.trading_interpretation ?? []).map((note) => (
              <Text key={note} style={{ fontSize: 13, color: '#374151' }}>
                {note}
              </Text>
            ))}
          </div>

          {imageUrl && <Button
            size="small"
            icon={<FileImageOutlined />}
            href={imageUrl}
            target="_blank"
            style={{ marginTop: 12 }}
          >
            查看降水原图
          </Button>}
        </div>

        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            overflow: 'hidden',
            minHeight: 132,
          }}
        >
          {imageUrl ? <Image
            src={imageUrl}
            alt="气象局降水预报图"
            width="100%"
            height={132}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            preview={{ src: imageUrl }}
          /> : <Empty description="本期无可用降水预报图" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </div>
    </section>
  );
};

export default WeatherBulletinValidationCard;
